import { cn } from "@/lib/utils";
import { Lock, ShieldCheck, XCircle } from "lucide-react";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FeatureFlags } from "@/constants/featureFlags";
import { useEffect } from "react";
import { trackAmplitudeEvent } from "@/utils/amplitude";
import { AnalyticsEventType } from "@/constants/analytics";

const CHECKOUT_TRUST_SIGNALS = [
  { label: "Secure Checkout, Powered by", icon: Lock },
  { label: "No lock-in. Cancel anytime.", icon: XCircle },
  { label: "Easy refunds", icon: ShieldCheck }
] as const;

/** PAYG forfeits unused prepaid time, so refunds only hold for Duration. */
const PAYG_EXCLUDED_TRUST_SIGNALS: readonly string[] = ["Easy refunds"];

/** Required at the point of payment, so not gated behind the experiment. */
const PAYG_BILLING_TERMS = [
  "Billing starts with the first prepaid hour, then continues in 5-minute prepaid intervals.",
  "Your hourly rate isn’t locked — future charges use the live rate at the time of charge.",
  "If you stop the cluster, any unused time in the current prepaid interval won’t carry over or be refunded.",
  "If your credit balance is too low to cover the next billing interval, your cluster may be automatically terminated. This may result in loss of unsaved or in-progress data.",
  "By selecting Pay & Deploy, you acknowledge and agree to these billing terms."
] as const;

function getPaymentProviderFeeLabel(currency: "iocoin" | "usdc" | "credits" | "stripe") {
  return currency === "stripe" ? "Payment provider fee: 5%" : "Payment provider fee: 0%";
}

type CheckoutTrustSignalsProps = {
  className?: string;
  currency: "iocoin" | "usdc" | "credits" | "stripe";
  topUpRequired: boolean;
  isPayg?: boolean;
};

export const CheckoutTrustSignals: React.FC<CheckoutTrustSignalsProps> = ({
  className,
  currency,
  topUpRequired,
  isPayg = false
}) => {
  const variant = useFeatureFlag(FeatureFlags.PAYMENT_TRUST_SIGNALS);

  useEffect(() => {
    if (variant && variant.value !== "control") {
      trackAmplitudeEvent(AnalyticsEventType.PAYMENT_TRUST_SIGNALS_SHOWN, {
        variant: variant.value,
        payload: variant.payload
      });
    }
  }, [variant]);

  const showTrustSignals = Boolean(variant) && variant?.value !== "control";

  if (!showTrustSignals && !isPayg) {
    return null;
  }

  const signals = CHECKOUT_TRUST_SIGNALS.filter(
    ({ label }) => !isPayg || !PAYG_EXCLUDED_TRUST_SIGNALS.includes(label)
  );

  const showSignalList = showTrustSignals && topUpRequired;

  return (
    <div
      className={cn(
        "rounded-lg bg-gradient-to-r from-green-light-500/15 to-transparent px-4 py-3",
        className
      )}
    >
      {showSignalList && (
        <>
          <ul className="m-0 flex flex-col gap-2">
            {signals.map(({ label, icon: Icon }) => (
              <li key={label} className="list-none">
                <div className="flex h-[22px] flex-shrink-0 items-center gap-1.5 rounded-md bg-transparent">
                  <Icon className="h-4 w-4 text-[#2D914F]" />
                  <span className="subtext-x-small text-foreground">{label}</span>
                  {label.includes("Powered by") &&
                    (currency === "stripe" || currency === "credits") && (
                      <img
                        src="/assets/images/stripe-logo.svg"
                        alt="Stripe"
                        className="ml-[-6px] h-[22px] w-auto object-contain"
                      />
                    )}
                  {label.includes("Powered by") &&
                    (currency === "iocoin" || currency === "usdc") && (
                      <img
                        src="/assets/images/sphere-pay-logo.svg"
                        alt="Sphere Pay"
                        className=" h-[16px] w-auto object-contain"
                      />
                    )}
                </div>
              </li>
            ))}
          </ul>
          <div className="subtext-x-small mt-4 text-gray-dark-2200">
            {getPaymentProviderFeeLabel(currency)}
          </div>
        </>
      )}
      {isPayg ? (
        <ul className={cn("m-0 flex list-disc flex-col gap-1 pl-4", showSignalList && "mt-2")}>
          {PAYG_BILLING_TERMS.map((term) => (
            <li key={term} className="subtext-x-small text-gray-dark-2200">
              {term}
            </li>
          ))}
        </ul>
      ) : (
        <div className={cn("subtext-x-small text-gray-dark-2200", topUpRequired && "mt-2")}>
          Refunds apply only to unused hours. The first hour is non-refundable.
        </div>
      )}
    </div>
  );
};
